
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronLeft, ChevronRight, ChevronDown, Target, User, Building2, Briefcase } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface GoalTemplate {
  id: string;
  role: string;
  department: string;
  description: string;
  goals: {
    goal_title: string;
    goal_description: string;
    goal_category: 'financial' | 'project_delivery' | 'customer_impact' | 'innovation' | 'leadership_behaviors';
    goal_type: 'performance' | 'behavioral';
  }[];
}

interface GoalsTemplateCarouselProps {
  onUseTemplate: (template: GoalTemplate) => void;
  isDisabled?: boolean;
}

export const GoalsTemplateCarousel = ({
  onUseTemplate,
  isDisabled = false
}: GoalsTemplateCarouselProps) => {
  const [templates, setTemplates] = useState<GoalTemplate[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Force collapse when disabled
  const isCollapsibleOpen = isDisabled ? false : isOpen;

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('goals_templates')
        .select('*')
        .order('role', { ascending: true });

      if (error) throw error;
      
      const formattedTemplates = (data || []).map(template => ({
        id: template.id,
        role: template.role,
        department: template.department,
        description: template.description,
        goals: template.goals as GoalTemplate['goals']
      }));
      
      setTemplates(formattedTemplates);
      
      if (formattedTemplates.length > 0 && currentIndex >= formattedTemplates.length) {
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error("Failed to load goal templates");
    } finally {
      setIsLoading(false);
    }
  };
  
  const nextTemplate = () => {
    if (!isDisabled && templates.length > 0) {
      setCurrentIndex(prev => (prev + 1) % templates.length);
    }
  };
  
  const prevTemplate = () => {
    if (!isDisabled && templates.length > 0) {
      setCurrentIndex(prev => (prev - 1 + templates.length) % templates.length);
    }
  };
  
  const handleOpenChange = (open: boolean) => {
    if (!isDisabled) {
      setIsOpen(open);
    }
  };
  
  const formatCategoryName = (category: string) => {
    return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-primary animate-pulse">Loading goal templates...</div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="rounded-xl border-2 border-primary/30 px-4 py-6 text-center">
        <p className="text-gray-400">No goal templates available. Contact your administrator to upload templates.</p>
      </div>
    );
  }
  
  const currentTemplate = templates[currentIndex];
  
  return (
    <Collapsible open={isCollapsibleOpen} onOpenChange={handleOpenChange}>
      <div className="flex items-center justify-between mb-2 rounded-xl border-2 border-primary px-4 py-2">
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className={`flex items-center gap-2 text-white p-0 w-full ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isDisabled}
          >
            <div className="flex items-center gap-2 w-full">
              <span className="text-[20px]">Goals Templates</span>
              {isDisabled && <span className="text-sm text-gray-400">(Disabled - Goals Frozen)</span>}
              <span className="text-sm text-gray-400">(Supervisors can use role templates to expedite goal setting)</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>{isDisabled ? "Cannot expand when goals are frozen" : "Click to collapse or expand"}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isCollapsibleOpen ? 'rotate-180' : ''}`} />
            </div>
          </Button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent>
        <div className={`relative ${isDisabled ? 'opacity-50' : ''}`}>
          <Card className="rounded-xl border border-primary/30 bg-none">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-primary text-lg px-0 mx-0 w-[300px]">{currentTemplate.role}</CardTitle>
                      <Button 
                        onClick={() => onUseTemplate(currentTemplate)} 
                        className="bg-primary hover:bg-primary/90 text-black font-medium text-sm h-8"
                        disabled={isDisabled}
                      >
                        Use This Template
                      </Button>
                    </div>
                    <CardDescription className="text-primary-light flex items-center gap-2 mt-1 text-sm">
                      {currentTemplate.department}
                    </CardDescription>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={prevTemplate} 
                    className="h-7 w-7 border-primary/30 text-primary hover:bg-primary/20"
                    disabled={isDisabled}
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  
                  <span className="text-xs text-gray-400 px-2">
                    {currentIndex + 1} / {templates.length}
                  </span>
                  
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={nextTemplate} 
                    className="h-7 w-7 border-primary/30 text-primary hover:bg-primary/20"
                    disabled={isDisabled}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              
              <p className="text-primary text-xs mt-1">{currentTemplate.description}</p>
            </CardHeader>
            
            <CardContent className="space-y-3 h-[330px]">
              <div className="space-y-2">
                <h4 className="text-white font-medium flex items-center gap-2 text-sm">
                  Goals for this role ({currentTemplate.goals.length} goals)
                </h4>
                
                <div className="space-y-1.5 max-h-48">
                  {currentTemplate.goals.map((goal, index) => (
                    <div key={index} className="p-2 bg-secondary rounded-lg border border-primary-light/30">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h5 className="text-primary-light font-medium text-xs leading-tight">{goal.goal_title}</h5>
                        <div className="flex gap-1 flex-shrink-0">
                          <Badge variant="outline" className="text-[10px] h-4 px-1 text-primary border-primary/50">
                            {formatCategoryName(goal.goal_category)}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] h-4 px-1 text-purple-400 border-purple-500/50">
                            {goal.goal_type}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-gray-400 text-[10px] leading-tight line-clamp-2">{goal.goal_description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
